import type { AccountActionTypes } from '@/store/actions/accountActions';
import {
  CLEAR_ACCOUNT_ERROR,
  FETCH_ACCOUNT_SUMMARY_FAILURE,
  FETCH_ACCOUNT_SUMMARY_REQUEST,
  FETCH_ACCOUNT_SUMMARY_SUCCESS,
  FETCH_ACCOUNTS_FAILURE,
  FETCH_ACCOUNTS_REQUEST,
  FETCH_ACCOUNTS_SUCCESS,
  FETCH_RECENT_TRANSACTIONS_FAILURE,
  FETCH_RECENT_TRANSACTIONS_REQUEST,
  FETCH_RECENT_TRANSACTIONS_SUCCESS,
  FETCH_USER_PROFILE_FAILURE,
  FETCH_USER_PROFILE_REQUEST,
  FETCH_USER_PROFILE_SUCCESS,
  LOGOUT_REQUEST,
  RESET_ACCOUNT_STATE,
  SET_SELECTED_ACCOUNT,
} from '@/store/actions/accountActions';
import type { AccountState, MonthlySpend, SpendCategory, Transaction } from '@/types/account.types';

const SPEND_CATEGORY_COLORS = [
  '#0b4da2',
  '#3a7bd5',
  '#0f8b57',
  '#e07b39',
  '#7b5ea7',
  '#dc3545',
  '#b38600',
  '#2aa198',
];

const SPEND_CATEGORY_RULES: Array<{ name: string; keywords: string[] }> = [
  { name: 'Mobile Recharge', keywords: ['recharge', 'prepaid', 'mobile', 'airtel', 'jio', 'vodafone', 'bsnl'] },
  { name: 'Grocery Payment', keywords: ['grocery', 'groceries', 'supermarket', 'bigbasket', 'blinkit', 'zepto', 'dmart', 'kirana'] },
  { name: 'Food & Dining', keywords: ['food', 'restaurant', 'swiggy', 'zomato', 'cafe', 'pizza', 'burger', 'dining'] },
  { name: 'Shopping', keywords: ['shopping', 'amazon', 'flipkart', 'myntra', 'ajio', 'mall'] },
  { name: 'Fuel', keywords: ['fuel', 'petrol', 'diesel'] },
  { name: 'Utility Bills', keywords: ['electricity', 'utility', 'water', 'broadband', 'internet', 'dth', 'bill'] },
  { name: 'Travel', keywords: ['travel', 'uber', 'ola', 'taxi', 'cab', 'flight', 'train', 'irctc', 'metro'] },
  { name: 'Entertainment', keywords: ['movie', 'cinema', 'netflix', 'spotify', 'hotstar', 'bookmyshow', 'entertainment'] },
  { name: 'Healthcare', keywords: ['hospital', 'pharmacy', 'medical', 'clinic', 'medicine'] },
  { name: 'Transfer', keywords: ['transfer', 'upi', 'neft', 'imps', 'rtgs'] },
];

const categorizeDebit = (transaction: Transaction): string => {
  const text = `${transaction.title} ${transaction.beneficiary}`.toLowerCase();
  const rule = SPEND_CATEGORY_RULES.find((r) => r.keywords.some((keyword) => text.includes(keyword)));
  return rule ? rule.name : 'Other';
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const buildMonthlySpend = (transactions: Transaction[]): MonthlySpend | null => {
  const now = new Date();
  const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const debits = transactions.filter(
    (t) => t.type === 'DEBIT' && t.date.startsWith(monthPrefix)
  );
  if (debits.length === 0) {
    return null;
  }

  const totalDebits = debits.reduce((sum, t) => sum + t.amount, 0);

  const byCategory = new Map<string, number>();
  debits.forEach((t) => {
    const key = categorizeDebit(t);
    byCategory.set(key, (byCategory.get(key) || 0) + t.amount);
  });

  const categories: SpendCategory[] = Array.from(byCategory.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, amount], index) => ({
      name,
      amount,
      color: SPEND_CATEGORY_COLORS[index % SPEND_CATEGORY_COLORS.length],
    }));

  return {
    month: MONTH_NAMES[now.getMonth()],
    year: now.getFullYear(),
    totalSpend: totalDebits,
    totalDebits,
    currency: 'INR',
    categories,
  };
};

const initialState: AccountState = {
  loading: false,
  accounts: [],
  selectedAccount: null,
  transactions: [],
  monthlySpend: null,
  user: null,
  userError: null,
  error: null,
};

const accountReducer = (state = initialState, action: AccountActionTypes): AccountState => {
  switch (action.type) {
    case FETCH_ACCOUNT_SUMMARY_REQUEST:
    case FETCH_ACCOUNTS_REQUEST:
    case FETCH_RECENT_TRANSACTIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FETCH_USER_PROFILE_REQUEST:
      return {
        ...state,
        loading: true,
        userError: null,
      };

    case FETCH_ACCOUNT_SUMMARY_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        accounts: action.payload.accounts,
        selectedAccount:
          action.payload.selectedAccount ||
          (action.payload.accounts.length > 0 ? action.payload.accounts[0] : null),
      };

    case FETCH_ACCOUNTS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        accounts: action.payload,
        selectedAccount: state.selectedAccount || (action.payload.length > 0 ? action.payload[0] : null),
      };

    case FETCH_RECENT_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        transactions: action.payload,
        monthlySpend: buildMonthlySpend(action.payload),
      };

    case FETCH_USER_PROFILE_SUCCESS:
      return {
        ...state,
        loading: false,
        userError: null,
        user: action.payload,
      };

    case FETCH_ACCOUNT_SUMMARY_FAILURE:
    case FETCH_ACCOUNTS_FAILURE:
    case FETCH_RECENT_TRANSACTIONS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case FETCH_USER_PROFILE_FAILURE:
      return {
        ...state,
        loading: false,
        userError: action.payload,
      };

    case SET_SELECTED_ACCOUNT:
      return {
        ...state,
        selectedAccount: action.payload,
      };

    case CLEAR_ACCOUNT_ERROR:
      return {
        ...state,
        error: null,
        userError: null,
      };

    case RESET_ACCOUNT_STATE:
    case LOGOUT_REQUEST:
      return {
        ...initialState,
        userError: null,
      };

    default:
      return state;
  }
};

export default accountReducer;
